import React, { useMemo, useState, useCallback } from 'react';
import { buildOnSiteSunburstTree } from '../../utils/whosIn/buildSunburstTree';
import './WhosInSunburst.css';

const SUNBURST_PALETTE = [
  '#f9be0b', '#f59e0b', '#fbbf24', '#d4a008', '#eab308', '#ca8a04',
  '#fde047', '#fcd34d', '#fb923c', '#f97316', '#ea580c', '#c2410c',
  '#14b8a6', '#2dd4bf', '#0d9488', '#22c55e', '#84cc16', '#65a30d',
  '#a3e635', '#4ade80', '#f472b6', '#ec4899', '#e879f9', '#c026d3',
  '#818cf8', '#6366f1', '#60a5fa', '#38bdf8', '#0284c7',
  '#f87171', '#ef4444', '#dc2626', '#78716c', '#a8a29e',
];

function arcPath(cx, cy, r0, r1, startAngle, endAngle) {
  if (endAngle - startAngle >= 2 * Math.PI - 0.001) {
    endAngle = startAngle + 2 * Math.PI - 0.001;
  }
  const x0o = cx + r1 * Math.cos(startAngle);
  const y0o = cy + r1 * Math.sin(startAngle);
  const x1o = cx + r1 * Math.cos(endAngle);
  const y1o = cy + r1 * Math.sin(endAngle);
  const x0i = cx + r0 * Math.cos(endAngle);
  const y0i = cy + r0 * Math.sin(endAngle);
  const x1i = cx + r0 * Math.cos(startAngle);
  const y1i = cy + r0 * Math.sin(startAngle);
  const large = endAngle - startAngle > Math.PI ? 1 : 0;
  return [
    `M ${x0o} ${y0o}`,
    `A ${r1} ${r1} 0 ${large} 1 ${x1o} ${y1o}`,
    `L ${x0i} ${y0i}`,
    `A ${r0} ${r0} 0 ${large} 0 ${x1i} ${y1i}`,
    'Z',
  ].join(' ');
}

function maxDepth(node, d = 0) {
  if (!node.children?.length) return d;
  return Math.max(...node.children.map((c) => maxDepth(c, d + 1)));
}

function layoutSegments(focusNode, size) {
  const depthLimit = Math.max(1, maxDepth(focusNode));
  const cx = size / 2;
  const cy = size / 2;
  const R = size / 2 - 8;
  const segments = [];

  function walk(node, startAngle, endAngle, depth) {
    if (depth > 0) {
      const r0 = ((depth - 1) / depthLimit) * R;
      const r1 = (depth / depthLimit) * R;
      segments.push({ node, startAngle, endAngle, r0, r1, depth, cx, cy });
    }
    if (!node.children?.length) return;
    const total = node.children.reduce((s, c) => s + (c.value || 0), 0) || 1;
    let angle = startAngle;
    node.children.forEach((child) => {
      const span = (endAngle - startAngle) * ((child.value || 0) / total);
      walk(child, angle, angle + span, depth + 1);
      angle += span;
    });
  }

  walk(focusNode, -Math.PI / 2, (3 * Math.PI) / 2, 0);
  return { segments, cx, cy, R, depthLimit };
}

function WhosInSunburst({ rows, viewDate }) {
  const tree = useMemo(() => buildOnSiteSunburstTree(rows), [rows]);
  const [focusPath, setFocusPath] = useState([]);
  const [hovered, setHovered] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const focusNode = useMemo(() => {
    let node = tree;
    focusPath.forEach((name) => {
      const next = node.children?.find((c) => c.name === name);
      if (next) node = next;
    });
    return node;
  }, [tree, focusPath]);

  const parentNode = useMemo(() => {
    if (focusPath.length === 0) return null;
    let node = tree;
    for (let i = 0; i < focusPath.length - 1; i += 1) {
      node = node.children?.find((c) => c.name === focusPath[i]) || node;
    }
    return node;
  }, [tree, focusPath]);

  const size = 420;
  const { segments, cx, cy, R } = useMemo(
    () => layoutSegments(focusNode, size),
    [focusNode],
  );

  const colorIndex = useCallback((seg, index = 0) => {
    const key = `${seg.node.category || ''}-${seg.node.department || ''}-${seg.node.name}`;
    let hash = index;
    for (let i = 0; i < key.length; i += 1) {
      hash = (hash + key.charCodeAt(i) * (i + 7)) % SUNBURST_PALETTE.length;
    }
    return SUNBURST_PALETTE[hash];
  }, []);

  const handleSegmentClick = (node) => {
    if (node.children?.length) {
      setFocusPath((p) => [...p, node.name]);
    }
  };

  const handleCenterClick = () => {
    if (focusPath.length > 0) setFocusPath((p) => p.slice(0, -1));
  };

  if (!rows.length) {
    return (
      <div className="whos-in-sunburst-empty">
        <p>No on-site people to chart for {viewDate}.</p>
      </div>
    );
  }

  return (
    <div className="whos-in-sunburst-wrap">
      <div className="whos-in-sunburst-breadcrumb">
        <button type="button" onClick={() => setFocusPath([])} className="whos-in-bc-btn">
          On site ({tree.value})
        </button>
        {focusPath.map((name, i) => (
          <React.Fragment key={name}>
            <span className="whos-in-bc-sep">›</span>
            <button
              type="button"
              className="whos-in-bc-btn"
              onClick={() => setFocusPath(focusPath.slice(0, i + 1))}
            >
              {name}
            </button>
          </React.Fragment>
        ))}
      </div>

      <div className="whos-in-sunburst-chart">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img">
          {segments.map((seg, index) => {
            const fill = colorIndex(seg, index);
            const isHovered = hovered?.node === seg.node;
            return (
              <path
                key={`${seg.node.name}-${seg.depth}-${seg.startAngle}`}
                d={arcPath(cx, cy, seg.r0, seg.r1, seg.startAngle, seg.endAngle)}
                fill={fill}
                fillOpacity={isHovered ? 1 : 0.88}
                stroke="#111827"
                strokeWidth={1.5}
                className="whos-in-sunburst-segment"
                onClick={() => handleSegmentClick(seg.node)}
                onMouseEnter={(e) => {
                  setHovered(seg);
                  setTooltipPos({ x: e.clientX, y: e.clientY });
                }}
                onMouseMove={(e) => setTooltipPos({ x: e.clientX, y: e.clientY })}
                onMouseLeave={() => setHovered(null)}
              />
            );
          })}
          <circle
            cx={cx}
            cy={cy}
            r={R * 0.22}
            className="whos-in-sunburst-center"
            onClick={handleCenterClick}
          />
          <text x={cx} y={cy - 6} textAnchor="middle" className="whos-in-sunburst-center-label">
            {focusNode.name}
          </text>
          <text x={cx} y={cy + 14} textAnchor="middle" className="whos-in-sunburst-center-count">
            {focusNode.value}
          </text>
        </svg>

        {hovered && (
          <div
            className="whos-in-sunburst-tooltip"
            style={{ left: tooltipPos.x + 12, top: tooltipPos.y + 12 }}
          >
            <strong>{hovered.node.name}</strong>
            <span>{hovered.node.value} people</span>
            {hovered.node.people?.length > 0 && (
              <span className="whos-in-sunburst-tooltip-names">
                {hovered.node.people.slice(0, 5).join(', ')}
                {hovered.node.people.length > 5 ? ` +${hovered.node.people.length - 5}` : ''}
              </span>
            )}
            {hovered.node.children?.length > 0 && (
              <span className="whos-in-sunburst-tooltip-hint">Click to zoom in</span>
            )}
          </div>
        )}
      </div>

      <p className="hint whos-in-sunburst-hint">
        Click a segment to zoom into department or team. Click the center to zoom out
        {parentNode ? ` to ${parentNode.name}` : ''}.
      </p>

      <div className="whos-in-sunburst-legend">
        {focusNode.children?.map((child, i) => (
          <div key={child.name} className="whos-in-sunburst-legend-item">
            <span
              className="whos-in-sunburst-legend-swatch"
              style={{ background: SUNBURST_PALETTE[i % SUNBURST_PALETTE.length] }}
            />
            <span>{child.name}</span>
            <span className="whos-in-sunburst-legend-count">({child.value})</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default WhosInSunburst;
